import { useEffect, useRef } from 'react'

const SIZES = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  // occupa quasi tutto lo schermo invece di restare stretta al centro: per i
  // form con molti campi, dove le due fasce laterali vuote non servono a
  // niente e tolgono solo spazio utile
  full: 'sm:max-w-[calc(100vw-2rem)] sm:h-[calc(100dvh-2rem)]',
}

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

/**
 * Overlay + box centrato usati (quasi) uguali a mano in una dozzina di file:
 * qui in un solo posto, con Esc e un focus-trap minimo che prima non c'era
 * quasi da nessuna parte (punto u1 della checklist di raffinamento).
 *
 * Il contenuto (form, scroll, footer) resta al chiamante: il Modal possiede
 * solo la cornice (overlay, box, intestazione con ×), non sa se dentro c'è
 * un form da inviare o un semplice messaggio.
 */
export default function Modal({
  onClose, title, subtitle, size = 'md', closeOnOverlayClick = true, children,
}) {
  const boxRef = useRef(null)
  const titleId = useRef(`modal-title-${Math.random().toString(36).slice(2)}`).current

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key !== 'Tab' || !boxRef.current) return
      const focusable = boxRef.current.querySelectorAll(FOCUSABLE)
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKey)

    const previouslyFocused = document.activeElement
    const first = boxRef.current?.querySelector(FOCUSABLE)
    first?.focus()

    return () => {
      window.removeEventListener('keydown', onKey)
      previouslyFocused?.focus?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={closeOnOverlayClick ? onClose : undefined}
      role="presentation"
    >
      <div
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={`bg-white w-full ${SIZES[size] || SIZES.md} rounded-t-2xl sm:rounded-xl shadow-xl flex flex-col max-h-[92dvh] sm:max-h-[calc(100dvh-2rem)]`}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <header className="flex items-start justify-between px-5 py-4 border-b border-gray-100 shrink-0">
            <div>
              <h3 id={titleId} className="font-semibold text-gray-900">
                {title}
              </h3>
              {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Chiudi"
              className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            >
              ×
            </button>
          </header>
        )}
        {children}
      </div>
    </div>
  )
}
